/**
 * PreloadedTruckProfile — public profile page for a preloaded truck.
 *
 * REGIONAL_PARITY_EXEMPT — this file is one of exactly THREE places
 * in the codebase where "preloaded" state is legitimately referenced.
 * The other two are `backend/server.py::_autocreate_profile_draft_from_preload`
 * (claim persistence) and `frontend/src/pages/AdminPreloadedTrucks.jsx`
 * (admin claim queue). After a truck is claimed here, it becomes a
 * normal TruckTrackr truck — no downstream feature branches on its
 * preloaded origin. See `/app/memory/PRD.md` → Core product principle
 * → Regional Parity.
 *
 * URL pattern: `/food-trucks/<city>/<slug>`. SEO-indexable on purpose
 * (per user spec — they want "Austin food truck name" Google queries
 * to surface this profile).
 *
 * Two render modes per spec 2c:
 *   - `coming_soon` → minimal stub with a giant "Claim this truck"
 *     CTA. Less SEO juice but lower expectation-setting risk.
 *   - any other status (`unclaimed`, `claimed`, `active`) → full
 *     styled profile with cuisine, links, food truck park, etc.
 *
 * Claim flow:
 *   - logged-in owner clicks Claim → POST /preloaded-trucks/.../claim
 *   - if claim email matches the CSV email → auto-verified, status
 *     flips to "claimed". Otherwise → pending + admin review.
 *   - logged-out users are bounced to /signup with a `?claim=<slug>`
 *     query so they can come back after signup.
 */
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../lib/auth";
import api, { formatApiError } from "../lib/api";
import {
  Truck, MapPin, InstagramLogo, FacebookLogo, GlobeHemisphereWest,
  Phone, Storefront, CheckCircle, Sparkle,
} from "@phosphor-icons/react";
import FoundingCityBadge from "../components/FoundingCityBadge";

export default function PreloadedTruckProfile() {
  const { city, slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimEmail, setClaimEmail] = useState("");
  const [showEmailField, setShowEmailField] = useState(false);
  // Optional proof-of-ownership fields — shown when the claimant
  // wants to help admin verify faster. Sent verbatim on POST /claim.
  const [showVerifyFields, setShowVerifyFields] = useState(false);
  const [verifyFacebook, setVerifyFacebook] = useState("");
  const [verifyInstagram, setVerifyInstagram] = useState("");
  const [verifyWebsite, setVerifyWebsite] = useState("");
  const [verifyNote, setVerifyNote] = useState("");

  useEffect(() => {
    if (!city || !slug) return;
    document.title = `Loading… · TruckTrackr`;
    let cancelled = false;
    (async () => {
      try {
        const [r, s] = await Promise.all([
          api.get(`/preloaded-trucks/by-slug/${city}/${slug}`),
          api.get(`/preloaded-trucks/stats?city=${city}`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setDoc(r.data);
        setStats(s.data || null);
        document.title = `${r.data.truck_name} · ${r.data.city} · TruckTrackr`;
      } catch (e) {
        if (cancelled) return;
        setErr(e?.response?.status === 404 ? "Truck not found" : "Couldn't load this truck.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [city, slug]);

  const handleClaim = async () => {
    if (!user) {
      navigate(`/signup?claim=${city}/${slug}&next=/food-trucks/${city}/${slug}`);
      return;
    }
    setClaiming(true);
    try {
      const payload = {};
      if (showEmailField && claimEmail.trim()) {
        payload.claim_email = claimEmail.trim();
      }
      if (verifyFacebook.trim()) payload.verify_facebook_url = verifyFacebook.trim();
      if (verifyInstagram.trim()) payload.verify_instagram_url = verifyInstagram.trim();
      if (verifyWebsite.trim()) payload.verify_website_url = verifyWebsite.trim();
      if (verifyNote.trim()) payload.verify_note = verifyNote.trim();
      const r = await api.post(
        `/preloaded-trucks/${city}/${slug}/claim`,
        payload,
      );
      setDoc(r.data);
      if (r.data.auto_verified) {
        toast.success("Claim verified — this truck is yours.", {
          description: "Check your email — we sent a confirmation with next steps.",
        });
      } else {
        toast.success("Claim submitted for review.", {
          description: "Confirmation email sent. Our team verifies within 1–2 business days.",
        });
      }
    } catch (e) {
      const detail = formatApiError(e?.response?.data?.detail);
      toast.error(detail || "Couldn't submit the claim. Try again.");
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#fdf5e6] flex items-center justify-center text-amber-900/60 text-sm">Loading…</div>;
  }
  if (err || !doc) {
    return (
      <div className="min-h-screen bg-[#fdf5e6] flex items-center justify-center p-6">
        <div className="text-center space-y-3 max-w-sm">
          <h1 className="text-xl font-semibold">{err}</h1>
          <Link to={`/food-trucks/${city}`} className="text-[#ea580c] underline font-medium">
            Back to the {city} list
          </Link>
        </div>
      </div>
    );
  }

  const isComingSoon = doc.status === "coming_soon";
  const isVerified = doc.claim_status === "verified";
  const isPending = doc.claim_status === "pending";
  // Display-cased city name for body copy.
  // `doc.city` is stored title-cased at import time (e.g. "Philadelphia");
  // if that's missing we fall back to slug → title-case ("philadelphia" → "Philadelphia").
  const citySlug = doc.city_slug || city || "";
  const cityDisplay = doc.city
    || (citySlug ? citySlug.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") : "your city");

  return (
    <div className="min-h-screen bg-[#fdf5e6] text-amber-950 relative" data-testid="preloaded-truck-profile">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, #92400e 1px, transparent 0)",
          backgroundSize: "20px 20px",
        }}
        aria-hidden="true"
      />
      <main className="relative max-w-2xl mx-auto px-5 sm:px-8 py-10 sm:py-14 space-y-8">
        {/* Breadcrumb — three levels (TruckTrackr → {City} trucks → {Truck}).
         * Both link segments navigate up the tree so a visitor arriving
         * from a Google search result never gets stranded. The current
         * page name is un-linked to satisfy WAI-ARIA breadcrumb pattern.
         * Also emits BreadcrumbList JSON-LD for Google's rich-result
         * eligibility (bare requirement for the crumbs to show as
         * "TruckTrackr › Philadelphia trucks › Slurp Philly" under the
         * blue link on SERPs). */}
        <nav
          className="text-xs text-amber-900/70 flex items-center gap-1.5 flex-wrap"
          aria-label="Breadcrumb"
          data-testid="preloaded-truck-breadcrumb"
        >
          <Link
            to="/"
            className="hover:text-[#ea580c] inline-flex items-center gap-1 font-medium"
            data-testid="breadcrumb-home"
          >
            <Truck weight="duotone" className="h-3.5 w-3.5" /> TruckTrackr
          </Link>
          <span className="text-amber-900/40" aria-hidden="true">/</span>
          <Link
            to={`/food-trucks/${citySlug}`}
            className="hover:text-[#ea580c]"
            data-testid="breadcrumb-city"
          >
            {cityDisplay} trucks
          </Link>
          <span className="text-amber-900/40" aria-hidden="true">/</span>
          <span
            className="text-amber-950/90 font-medium truncate max-w-[45%]"
            aria-current="page"
            title={doc.truck_name}
          >
            {doc.truck_name}
          </span>
        </nav>
        <script
          type="application/ld+json"
          // BreadcrumbList schema — the URLs are absolute per Google's spec.
          // `window.location.origin` at render time is sufficient because
          // this page is rendered client-side. When SSR/prerender lands
          // later, swap to the public base URL from env.
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "TruckTrackr",
                  item: (typeof window !== "undefined" ? window.location.origin : "https://thetrucktrackr.com") + "/",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: `${cityDisplay} trucks`,
                  item: (typeof window !== "undefined" ? window.location.origin : "https://thetrucktrackr.com") + `/food-trucks/${citySlug}`,
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: doc.truck_name,
                },
              ],
            }),
          }}
        />

        {/* Header */}
        <header className="space-y-3">
          {doc.priority_tier === "P0" && !isComingSoon && (
            <div className="inline-block text-[10px] font-bold tracking-[0.22em] uppercase text-[#ea580c] bg-[#ea580c]/10 px-2 py-1 rounded-full">
              ★ Featured
            </div>
          )}
          <h1 className="text-3xl sm:text-5xl font-black leading-[1.05] tracking-tight">
            {doc.truck_name}
          </h1>
          <div className="text-sm text-amber-900/80 inline-flex items-center gap-2 flex-wrap">
            {doc.cuisine_type && <span className="font-medium">{doc.cuisine_type}</span>}
            {doc.food_truck_park && (
              <>
                <span className="text-amber-300">·</span>
                <span className="inline-flex items-center gap-1">
                  <MapPin weight="duotone" className="h-4 w-4" /> {doc.food_truck_park}
                </span>
              </>
            )}
            {doc.service_area && (
              <>
                <span className="text-amber-300">·</span>
                <span>{doc.service_area}</span>
              </>
            )}
          </div>
          {isVerified && (
            <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full">
              <CheckCircle weight="fill" className="h-3.5 w-3.5" /> Owner-claimed
            </div>
          )}
          {isPending && (
            <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">
              Claim under review
            </div>
          )}
          {doc.founding_slot_number && doc.founding_city_slug && (
            // Permanent founding badge — shown on any preloaded truck
            // that won a launch-promo slot. Sticky for life. Per-city
            // color theme lives in `lib/foundingCityTheme.js` so each
            // market's founding cohort reads distinctly at a glance.
            <FoundingCityBadge
              citySlug={doc.founding_city_slug}
              slotNumber={doc.founding_slot_number}
              grantedAt={doc.founding_slot_granted_at}
            />
          )}
        </header>

        {/* Coming-soon stub mode: just a giant CTA + name. */}
        {isComingSoon ? (
          <section className="rounded-2xl bg-white/85 border-2 border-[#ea580c]/40 p-6 sm:p-8 space-y-4 shadow-sm" data-testid="profile-stub">
            <div className="flex items-center gap-2">
              <Sparkle weight="duotone" className="h-5 w-5 text-[#ea580c]" />
              <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#ea580c]">
                This profile is unclaimed
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold leading-snug">
              Own {doc.truck_name}?
            </h2>
            <FoundingProBanner stats={stats} variant="stub" />
            <p className="text-amber-950/80">
              We loaded this truck onto TruckTrackr to make it easy for {cityDisplay} customers to find you.
              Claim your profile to add real-time pins, a menu, and a custom schedule.
            </p>
            {!isVerified && (
              <ClaimWidget
                showEmailField={showEmailField}
                setShowEmailField={setShowEmailField}
                claimEmail={claimEmail}
                setClaimEmail={setClaimEmail}
                claiming={claiming}
                handleClaim={handleClaim}
                doc={doc}
                user={user}
                showVerifyFields={showVerifyFields}
                setShowVerifyFields={setShowVerifyFields}
                verifyFacebook={verifyFacebook}
                setVerifyFacebook={setVerifyFacebook}
                verifyInstagram={verifyInstagram}
                setVerifyInstagram={setVerifyInstagram}
                verifyWebsite={verifyWebsite}
                setVerifyWebsite={setVerifyWebsite}
                verifyNote={verifyNote}
                setVerifyNote={setVerifyNote}
              />
            )}
          </section>
        ) : (
          /* Full styled profile (unclaimed/claimed/active). */
          <>
            {doc.short_description && (
              <p className="text-base sm:text-lg leading-relaxed text-amber-950/85">
                {doc.short_description}
              </p>
            )}
            {(doc.website_url || doc.instagram_url || doc.facebook_url || doc.google_maps_url || doc.yelp_url || doc.phone) && (
              <section className="rounded-2xl bg-white/80 border border-amber-200 p-5 sm:p-6 space-y-2" data-testid="profile-links">
                <h2 className="text-[10px] font-bold tracking-[0.22em] uppercase text-amber-700 mb-2">
                  Where to find them
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {doc.website_url && <ProfileLink icon={GlobeHemisphereWest} href={doc.website_url} label="Website" />}
                  {doc.instagram_url && <ProfileLink icon={InstagramLogo} href={doc.instagram_url} label="Instagram" />}
                  {doc.facebook_url && <ProfileLink icon={FacebookLogo} href={doc.facebook_url} label="Facebook" />}
                  {doc.google_maps_url && <ProfileLink icon={MapPin} href={doc.google_maps_url} label="Google Maps" />}
                  {doc.yelp_url && <ProfileLink icon={Storefront} href={doc.yelp_url} label="Yelp" />}
                  {doc.phone && <ProfileLink icon={Phone} href={`tel:${doc.phone}`} label={doc.phone} />}
                </div>
              </section>
            )}
            {!isVerified && (
              <section className="rounded-2xl bg-[#ea580c] text-white p-5 sm:p-6 space-y-3 shadow-lg shadow-orange-900/15" data-testid="claim-banner">
                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-orange-100/90">
                  Owner of {doc.truck_name}?
                </div>
                <h2 className="text-xl sm:text-2xl font-bold leading-snug">
                  Claim this truck and go live in {cityDisplay}.
                </h2>
                <FoundingProBanner stats={stats} variant="banner" />
                <p className="text-sm text-orange-50/85 leading-relaxed">
                  Real-time pins, schedule posts, customer notifications &mdash; all yours.
                </p>
                <ClaimWidget
                  showEmailField={showEmailField}
                  setShowEmailField={setShowEmailField}
                  claimEmail={claimEmail}
                  setClaimEmail={setClaimEmail}
                  claiming={claiming}
                  handleClaim={handleClaim}
                  doc={doc}
                  user={user}
                  ctaVariant="dark"
                  showVerifyFields={showVerifyFields}
                  setShowVerifyFields={setShowVerifyFields}
                  verifyFacebook={verifyFacebook}
                  setVerifyFacebook={setVerifyFacebook}
                  verifyInstagram={verifyInstagram}
                  setVerifyInstagram={setVerifyInstagram}
                  verifyWebsite={verifyWebsite}
                  setVerifyWebsite={setVerifyWebsite}
                  verifyNote={verifyNote}
                  setVerifyNote={setVerifyNote}
                />
              </section>
            )}
          </>
        )}

        <footer className="pt-4 border-t border-amber-200/60 text-xs text-amber-900/70 space-y-1">
          <div>
            Part of TruckTrackr&rsquo;s {cityDisplay} expansion.{" "}
            <Link to={`/food-trucks/${citySlug}`} className="text-[#ea580c] underline">
              See all {cityDisplay} trucks &rarr;
            </Link>
          </div>
        </footer>
      </main>
    </div>
  );
}

function ProfileLink({ icon: Icon, href, label }) {
  return (
    <a
      href={href}
      target={href.startsWith("tel:") ? undefined : "_blank"}
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 text-sm text-amber-900 hover:text-[#ea580c] py-1.5 transition-colors"
    >
      <Icon weight="duotone" className="h-4 w-4 flex-none" />
      <span className="truncate">{label}</span>
    </a>
  );
}

function ClaimWidget({
  showEmailField, setShowEmailField, claimEmail, setClaimEmail,
  claiming, handleClaim, doc, user, ctaVariant,
  showVerifyFields, setShowVerifyFields,
  verifyFacebook, setVerifyFacebook,
  verifyInstagram, setVerifyInstagram,
  verifyWebsite, setVerifyWebsite,
  verifyNote, setVerifyNote,
}) {
  const ctaClass = ctaVariant === "dark"
    ? "bg-white text-[#ea580c] hover:bg-amber-50"
    : "bg-[#ea580c] text-white hover:bg-[#c2410c]";
  const isDark = ctaVariant === "dark";
  const verifyInputClass = isDark
    ? "h-10 bg-white/95 text-amber-950 text-sm"
    : "h-10 bg-white text-amber-950 text-sm";
  return (
    <div className="space-y-2" data-testid="claim-widget">
      {!user && (
        <p className={isDark ? "text-xs text-orange-50/85" : "text-xs text-amber-900/70"}>
          You&rsquo;ll sign up for a free TruckTrackr account on the next step.
        </p>
      )}
      {showEmailField && (
        <Input
          type="email"
          placeholder="Use the truck's listed email for instant verification"
          value={claimEmail}
          onChange={(e) => setClaimEmail(e.target.value)}
          className="h-11 bg-white/95 text-amber-950"
          data-testid="claim-email-input"
        />
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          onClick={handleClaim}
          disabled={claiming}
          className={`h-11 flex-1 font-bold ${ctaClass}`}
          data-testid="claim-button"
        >
          {claiming ? "Submitting…" : "Claim this truck"}
        </Button>
        {doc.has_email_on_file && !showEmailField && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => setShowEmailField(true)}
            className={`h-11 ${isDark ? "text-orange-50 hover:bg-orange-700/30" : "text-amber-900 hover:bg-amber-100"}`}
            data-testid="claim-add-email"
          >
            Use my truck&rsquo;s email →
          </Button>
        )}
      </div>
      {doc.has_email_on_file && (
        <p className={`text-[11px] leading-snug ${isDark ? "text-orange-100/75" : "text-amber-900/70"}`}>
          Matching the email on file? You&rsquo;re verified instantly. Different email? We&rsquo;ll review within 1–2 business days.
        </p>
      )}

      {/* Proof-of-ownership disclosure — collapsed by default so the
          primary CTA stays the focus. Once expanded, gives the claimant
          a way to speed up manual review by dropping public FB / IG /
          website links or a note. Everything here is optional. */}
      {setShowVerifyFields && (
        <div className="pt-1">
          {!showVerifyFields ? (
            <button
              type="button"
              onClick={() => setShowVerifyFields(true)}
              className={`text-[11px] underline underline-offset-2 ${isDark ? "text-orange-100/85 hover:text-white" : "text-amber-900/80 hover:text-amber-950"}`}
              data-testid="claim-add-verify-toggle"
            >
              Speed up review — add links that prove you run this truck
            </button>
          ) : (
            <div
              className={`mt-2 rounded-lg p-3 space-y-2 ${isDark ? "bg-white/10 border border-white/15" : "bg-amber-50 border border-amber-200"}`}
              data-testid="claim-verify-fields"
            >
              <div className={`text-[10px] uppercase tracking-[0.16em] font-bold ${isDark ? "text-orange-50/80" : "text-amber-700"}`}>
                Help us verify (optional)
              </div>
              <Input
                type="url"
                placeholder="Facebook page URL (e.g. facebook.com/yourtruck)"
                value={verifyFacebook}
                onChange={(e) => setVerifyFacebook(e.target.value)}
                className={verifyInputClass}
                data-testid="claim-verify-facebook"
              />
              <Input
                type="url"
                placeholder="Instagram profile URL"
                value={verifyInstagram}
                onChange={(e) => setVerifyInstagram(e.target.value)}
                className={verifyInputClass}
                data-testid="claim-verify-instagram"
              />
              <Input
                type="url"
                placeholder="Website URL"
                value={verifyWebsite}
                onChange={(e) => setVerifyWebsite(e.target.value)}
                className={verifyInputClass}
                data-testid="claim-verify-website"
              />
              <Input
                type="text"
                placeholder="Anything else that helps us verify (e.g. business owner name)"
                value={verifyNote}
                onChange={(e) => setVerifyNote(e.target.value)}
                className={verifyInputClass}
                data-testid="claim-verify-note"
              />
              <p className={`text-[10.5px] leading-snug ${isDark ? "text-orange-100/70" : "text-amber-900/70"}`}>
                Any link where the truck&rsquo;s name / logo / owner is publicly visible works. Speeds up admin review from days to hours.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/** FoundingProBanner — dynamic CTA snippet that swaps copy based on
 *  how many launch-promo slots are left. Two visual variants:
 *  - `stub`  — amber pill, used inside the "claim your stub" card
 *  - `banner` — high-contrast orange-on-orange, used inside the deep
 *               orange "Claim & go live" banner on the full profile.
 *  When the promo is exhausted, both variants morph into a soft
 *  "all spots claimed" message so the page is never misleading. */
function FoundingProBanner({ stats, variant = "stub" }) {
  if (!stats || !stats.founding_slots_total) return null;
  const total = stats.founding_slots_total;
  const available = stats.founding_slots_available;
  const active = stats.founding_promo_active;
  const label = stats.founding_promo_label || "Founding Pro";
  const isDark = variant === "banner";
  if (active && available > 0) {
    return (
      <div
        className={`rounded-lg p-3 sm:p-4 text-sm font-semibold leading-snug ${
          isDark
            ? "bg-orange-700/40 border border-orange-300/30 text-orange-50"
            : "bg-amber-100 border border-amber-300 text-amber-950"
        }`}
        data-testid="profile-founding-counter"
      >
        <span className={`text-[10px] font-bold uppercase tracking-[0.18em] block mb-1 ${isDark ? "text-orange-100" : "text-amber-700"}`}>
          ✨ {label} promo
        </span>
        <span className="font-bold text-base">{available} of {total}</span> founding-Pro spots remaining.
        Verify your claim and get free Pro {stats?.founding_term_days ? "for a full year" : "for life"} + a permanent &ldquo;{label} #N&rdquo; badge on your profile.
      </div>
    );
  }
  return (
    <div
      className={`rounded-lg p-3 sm:p-4 text-sm leading-snug ${
        isDark
          ? "bg-orange-900/30 border border-orange-300/20 text-orange-50/90"
          : "bg-emerald-50 border border-emerald-200 text-emerald-900"
      }`}
      data-testid="profile-founding-closed"
    >
      All {total} founding-Pro spots have been claimed. You can still claim this profile and unlock all Pro features
      via Stripe — just no founding badge.
    </div>
  );
}
